import React, { useCallback, useReducer, useMemo } from 'react';
import { useEngagementPageActionsApi } from '@metrics/client/react/useEngagementPageActionsApi';
import { LoggerFactory } from '@cvent/logging/LoggerFactory';
import useBreakpoints from '@hooks/useBreakpoints';
import {
  ENGAGEMENT_PANEL_ACTIONS,
  ENGAGEMENT_MESSAGE_TYPE,
  ENGAGEMENT_PANEL_IDS
} from './constants';

const LOG = LoggerFactory.create('EngagementMessagesProvider');
const toastsThatNeedCombining = [ENGAGEMENT_PANEL_IDS.PLANNER_NOTIFICATIONS];
const NUMBER_OF_TOAST_TO_SHOW = 3;
const AGENDA_ITEM_DISMISS_TIMEOUT = 600_000; // 10 minutes

export const ENGAGEMENT_MESSAGE_ACTIVATED_ANALYTICS_ID =
  'engagement_message_activated';
export const ENGAGEMENT_TOAST_MESSAGE_DISMISSED_ANALYTICS_ID =
  'engagement_toast_message_dismissed';

const initialState = {
  panelIds: [],
  messages: new Map(),
  activePanel: null,
  toastsCurrentlyDisplayed: 0,
  moderatorItem: null,
  agendaItem: null,
  toastMessages: [],
  maxToasts: NUMBER_OF_TOAST_TO_SHOW,
  toastContainerId: null
};

const reducer = (state, action, sendAnalytics = () => null) => {
  const {
    messages,
    panelIds,
    activePanel,
    toastsCurrentlyDisplayed,
    moderatorItem,
    agendaItem,
    maxToasts
  } = state;
  const { type, update, analyticsData } = action;

  // validate consumers are using the api correctly
  const validateMessage = () => {
    const { panelId, payload } = update;
    if (!panelId || !payload || !panelIds.includes(panelId)) {
      LOG.debug(
        'ERROR: We could not send your message to the engagement panel. Please verify you are using the api correctly.'
      );
      return false;
    }

    const handleMaxNumberOfToasts = (
      notiType = ENGAGEMENT_MESSAGE_TYPE.BADGE
    ) => {
      sendAnalytics(
        { ...analyticsData, wasMessageDisplayed: false },
        ENGAGEMENT_MESSAGE_TYPE.BADGE
      );
      LOG.debug(
        `The maximum number of toasts has been reached. A badge was added for ${panelId}`
      );

      return {
        panelId,
        payload: {
          ...payload,
          // We have 3 toasts already showing, let's show a badge instead
          type: notiType
        }
      };
    };

    // Only add badge or toast if the panel sending the message is different than the activePanel. In other words, only
    // show messages if the user is in a different context and only if the panel does not have a badge message already
    const msg = messages.get(panelId);
    const toasts = msg?.toasts || [];
    // To provide additional info in the facts to identify if a message was added or not to the panel.
    const wasMessageDisplayed = false;

    // If this is an Agenda Item Reminder message, we let it through and we send analytics if the consumer provides data
    if (panelId === ENGAGEMENT_PANEL_IDS.AGENDA_ITEM_REMINDERS) {
      if (analyticsData) {
        sendAnalytics(
          { ...analyticsData, wasMessageDisplayed: true },
          ENGAGEMENT_MESSAGE_TYPE.TOAST
        );
      }
      return {
        panelId,
        payload: {
          ...payload,
          type: ENGAGEMENT_MESSAGE_TYPE.TOAST
        }
      };
    }
    if (panelId === activePanel) {
      LOG.debug(
        `Your message was not added to the panel because the user is already in ${panelId}`
      );
      sendAnalytics(
        { ...analyticsData, wasMessageDisplayed },
        ENGAGEMENT_MESSAGE_TYPE.NONE
      );
      return false;
    }
    if (
      toastsCurrentlyDisplayed === maxToasts &&
      !toastsThatNeedCombining.includes(panelId)
    ) {
      return handleMaxNumberOfToasts();
    }
    if (payload.type === ENGAGEMENT_MESSAGE_TYPE.TOAST) {
      // Does this toast support combining content ?
      if (toastsThatNeedCombining.includes(panelId)) {
        if (toastsCurrentlyDisplayed === maxToasts) {
          return handleMaxNumberOfToasts(ENGAGEMENT_MESSAGE_TYPE.NONE);
        }

        LOG.debug(`Combining content for ${panelId}`, toasts);
        const curentData = toasts[0]?.data || [];
        const newData = payload.data || [];
        // Is the consumer also managing the messages? if it sends more than 1 then it is
        const combinedData =
          newData.length > 1 ? [...newData] : [...curentData, ...newData];

        return {
          panelId,
          isCombined: true,
          payload: {
            ...payload,
            data: combinedData,
            type: ENGAGEMENT_MESSAGE_TYPE.TOAST
          }
        };
      }
      if (toasts.length > 0 || msg?.badge?.count > 0) {
        // Is this panel already displaying a toast or does the panel have a badge already?
        LOG.debug(
          `Your message was not added to the panel becuase ${panelId} is showing a toast/badge already`
        );
        sendAnalytics(
          { ...analyticsData, wasMessageDisplayed },
          ENGAGEMENT_MESSAGE_TYPE.BADGE
        );
        return false;
      }
    }
    sendAnalytics(
      { ...analyticsData, wasMessageDisplayed: true },
      payload.type
    );
    return { panelId, payload };
  };

  const validateMessageView = () => {
    const { panelId, msgType = 'all' } = update;
    if (!panelId || !panelIds.includes(panelId)) {
      LOG.debug(
        'ERROR: Incorrect panelId. Please make sure you provide one of the panelIds given during initialization.',
        panelIds,
        panelId
      );

      return false;
    }

    return { panelId, msgType };
  };

  /**
   * Function to handle the business logic for agenda item messages.
   *
   * @param {*} newAgendaItem
   */
  const handleAgendaItemMessage = newAgendaItem => {
    const currentAgendaItem = agendaItem;
    const payload = newAgendaItem;
    let hasCombinedItems = false;

    if (currentAgendaItem) {
      hasCombinedItems = true;
      LOG.debug(
        'Combine existing agenda item with new one',
        currentAgendaItem,
        newAgendaItem
      );
    }

    const shouldIncreaseTotalToastCount =
      toastsCurrentlyDisplayed < NUMBER_OF_TOAST_TO_SHOW;

    return {
      agendaItemPayload: {
        ...payload,
        hasCombinedItems,
        panelId: ENGAGEMENT_PANEL_IDS.AGENDA_ITEM_REMINDERS,
        publishedAt: new Date(Date.now()),
        dismissTimeout: AGENDA_ITEM_DISMISS_TIMEOUT
      },
      shouldIncreaseTotalToastCount
    };
  };

  switch (type) {
    case ENGAGEMENT_PANEL_ACTIONS.SET_ACTIVE_PANEL:
      return {
        ...state,
        activePanel: action.activePanel
      };
    case ENGAGEMENT_PANEL_ACTIONS.NOTIFY_MESSAGE: {
      // validate data sent by consumers first
      const result = validateMessage();
      if (!result) {
        return state;
      }

      const { panelId, payload, isCombined = false } = result;
      // check message type is supported
      const validTypes = [
        ENGAGEMENT_MESSAGE_TYPE.BADGE,
        ENGAGEMENT_MESSAGE_TYPE.TOAST
      ];
      if (!validTypes.includes(payload.type)) {
        LOG.debug(`ERROR: Unsupported message type ${payload.type}`);
        return state;
      }

      const newMessages = new Map(messages);
      const msg = newMessages.get(panelId);
      // Deep copy the panel message
      const newMessage = JSON.parse(JSON.stringify(msg)) || {};
      const toasts = newMessage?.toasts || [];
      let badge = newMessage?.badge || { count: undefined };
      let totalToastsCount = toastsCurrentlyDisplayed || 0;
      let newAgendaItem = agendaItem;
      let newModeratorItem = moderatorItem;

      // If the panel got another badge update, check if we should increment the count
      if (payload.type === ENGAGEMENT_MESSAGE_TYPE.BADGE) {
        badge = {
          ...payload,
          panelId,
          publishedAt: new Date(Date.now()),
          count: newMessage?.badge?.count ? newMessage.badge.count + 1 : 1
        };
        newMessages.set(panelId, { badge, toasts });
      } else if (ENGAGEMENT_MESSAGE_TYPE.TOAST) {
        // Handle Agenda Items Separately
        if (panelId === ENGAGEMENT_PANEL_IDS.AGENDA_ITEM_REMINDERS) {
          const { agendaItemPayload, shouldIncreaseTotalToastCount } =
            handleAgendaItemMessage(payload);
          newAgendaItem = agendaItemPayload;
          totalToastsCount = shouldIncreaseTotalToastCount
            ? totalToastsCount + 1
            : totalToastsCount;
        } else if (panelId === ENGAGEMENT_PANEL_IDS.MODERATOR_ENGAGEMENT) {
          newModeratorItem = {
            ...payload,
            panelId,
            publishedAt: new Date(Date.now())
          };
          totalToastsCount++;
        } else {
          // Add the new toast message to the array
          if (isCombined) {
            // Replace toast with combined content
            toasts.splice(0, 1, {
              ...payload,
              panelId,
              publishedAt: new Date(Date.now())
            });
          } else {
            toasts.push({
              ...payload,
              panelId,
              publishedAt: new Date(Date.now())
            });
            totalToastsCount++;
          }
          // Update the message for this panel
          newMessages.set(panelId, { badge, toasts });
        }
      }

      // Return updated state
      return {
        ...state,
        moderatorItem: newModeratorItem,
        agendaItem: newAgendaItem,
        messages: newMessages,
        toastsCurrentlyDisplayed: totalToastsCount > 0 ? totalToastsCount : 0
      };
    }

    case ENGAGEMENT_PANEL_ACTIONS.CLEAR_MESSAGE: {
      // validate data sent by consumers first
      const view = validateMessageView();
      if (!view) {
        return state;
      }

      const { panelId, msgType } = view;
      const msg = messages.get(panelId) || { badge: null, toasts: [] };
      // If the message being deleted is for a toast then keep the badges
      const badge =
        msgType === ENGAGEMENT_MESSAGE_TYPE.TOAST ? msg.badge : null;
      // If the message being deleted is for a badge then keep the badges
      const toasts =
        msgType === ENGAGEMENT_MESSAGE_TYPE.BADGE ? msg.toasts : [];
      messages.set(panelId, { badge, toasts });

      return {
        ...state,
        messages,
        toastsCurrentlyDisplayed:
          toastsCurrentlyDisplayed > 0 ? toastsCurrentlyDisplayed - 1 : 0,
        moderatorItem:
          panelId === ENGAGEMENT_PANEL_IDS.MODERATOR_ENGAGEMENT
            ? null
            : moderatorItem,
        agendaItem:
          panelId === ENGAGEMENT_PANEL_IDS.AGENDA_ITEM_REMINDERS
            ? null
            : agendaItem
      };
    }

    case ENGAGEMENT_PANEL_ACTIONS.SET_TOAST_CONTAINER_ID: {
      const { toastContainerId } = update;
      return {
        ...state,
        toastContainerId
      };
    }

    default:
      return state;
  }
};
const EngagementMessagesContext = React.createContext(initialState);

/**
 * Component which exposes an api for consumers to easily send messages
 * to the engagement panel so that either badges or toast messages can be shown.
 *
 * The Engagement messages follow a set of rules which are used to determine how
 * messages are displayed in the panel. These rules are described below
 *
 * 1.- For the features that show a toast, we will also show a badge*.
 *   *for messages that show a toast, a badge is only shown when the toast is dismissed.
 * 2.- While the badge is activated, no additional toast shows up for that feature.
 * 3.- When the user opens the feature, the badge/toast disappears.
 * 4.- While the user is on the feature, no additional toast or badge appears for that feature.
 * 5.- In the unusual case that there are many notifications at the same time, we will display
 *     a maximum of 3 at the same time. The rest of the notifications will not be shown, however
 *     the badge of the corresponding functionality will be activated.
 * 6.- Moderator Engagement toast should always take first priority and be displayed at the top of the toast stack. If there is Agenda Item, then Agenda Item will be the first priority, then Moderator toast
 *
 * Agenda Items/Seamless Transitions Notifications Rules
 *
 * 1.- If we already have the max number of toast showing (3 for now) we then remove one of the engagement
 *     messages toast and the Seamless Transition message will take the first spot (first toast in stack)
 * 2.- If a message of this type already took one of the spots, newer Engagement messages shall not
 *     replace it while it is visible
 * 3.- These messages are only dismissed when
 *   - The user clicks the link in it
 *   - The user moves to a different page
 *   - The toast timeout kicks in (Default is 10 minutes for this type of messages)
 */
const EngagementMessagesProvider = ({
  children,
  panelIds,
  activePanel = null
}) => {
  const { engagementMessageActivated, engagementToastMessageDismissed } =
    useEngagementPageActionsApi();
  const { isMobile } = useBreakpoints();

  // If the application forces a render higher up in the tree (i.e. Any of the app providers/containers updates data),
  // then messages will get lost, meaning if a badge or toast being shown in the engagement panel it will dissapear
  // without the user clicking on it.

  const msgs = new Map();
  panelIds.forEach(panelId => msgs.set(panelId, null));

  const withAnalytics = useCallback(
    (state, action) => {
      const sendAnalytics = (data, payloadType) => {
        const analyticsId = data?.id;
        // Facts will be sent only if a consumer provides valid data
        if (
          analyticsId &&
          engagementMessageActivated &&
          engagementToastMessageDismissed
        ) {
          switch (analyticsId) {
            case ENGAGEMENT_MESSAGE_ACTIVATED_ANALYTICS_ID:
              engagementMessageActivated({ ...data, messageType: payloadType });
              break;
            case ENGAGEMENT_TOAST_MESSAGE_DISMISSED_ANALYTICS_ID:
              engagementToastMessageDismissed({
                ...data,
                messageType: payloadType
              });
              break;
            default:
              break;
          }
        }
      };

      return reducer(state, action, sendAnalytics);
    },
    [engagementMessageActivated, engagementToastMessageDismissed]
  );

  const [state, dispatch] = useReducer(withAnalytics, {
    panelIds,
    activePanel,
    messages: msgs,
    toastsCurrentlyDisplayed: 0,
    moderatorItem: null,
    agendaItem: null,
    toastMessages: [],
    maxToasts: isMobile ? 1 : 3,
    toastContainerId: null
  });
  const contextValue = useMemo(() => {
    return { state, dispatch };
  }, [state, dispatch]);

  return (
    <EngagementMessagesContext.Provider value={contextValue}>
      {children}
    </EngagementMessagesContext.Provider>
  );
};

export { EngagementMessagesContext, EngagementMessagesProvider };
